import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom'
import CurrentUserAvatar from '../UI/CurrentUserAvatar'
import UserAuthContainer from '../Containers/UserAuthContainer'
import ROUTES from '../../routesConfig'
import TimeAgoTranslator from '../Utils/TimeAgoTranslator'
import TokenStorage from '../Api/TokenStorage'
import anearApi from '../Api/AnearApi'
import useNavigation from '../Utils/navigationUtils'
import PageBackRow from '../UI/PageBackRow'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'

import {
  Alert,
  Box,
  CardActions,
  CardContent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Paper
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'

const timeAgoTranslator = new TimeAgoTranslator()
const tokenStorage = new TokenStorage()
const LinkStyling = {
  textDecoration: 'none'
}

const normalizeExternalUrl = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

const buildAccountTimingCopy = (userAuthContainer, { includeGuestExpiry } = {}) => {
  const user = userAuthContainer.anearUser()
  let createdCopy = null
  let createdRelative = null
  let guestExpiryCopy = null
  let guestExpiryRelative = null

  if (user) {
    const isRegistered = userAuthContainer.isRegisteredAuthed()
    let memberSince = null

    if (isRegistered) {
      // Registered users: always use original creation date
      memberSince = user.memberSince()
    } else {
      // Guests (or unknown auth type): prefer claimed_at, fallback to created_at
      memberSince = user.claimedAt() || user.memberSince()
    }

    if (memberSince) {
      const timeAgo = timeAgoTranslator.translate(memberSince)
      createdCopy = `Account created ${timeAgo}`
      createdRelative = timeAgo
    }
  }

  if (includeGuestExpiry) {
    try {
      const acctType = tokenStorage.acctType()
      const payload = tokenStorage.decodeAuthTokenPayload()

      if (acctType && acctType !== 'registered' && payload && payload.exp) {
        const expiresDate = new Date(payload.exp * 1000)
        const expiresAt = expiresDate.toISOString()
        const timeUntil = timeAgoTranslator.translate(expiresAt)
        const verb = expiresDate < new Date() ? 'expired' : 'expires'
        guestExpiryCopy = `Guest access ${verb} ${timeUntil}.`
        guestExpiryRelative = timeUntil
      }
    } catch (e) {
      // If anything goes wrong decoding the token, silently ignore.
      guestExpiryCopy = null
    }
  }

  return { createdCopy, createdRelative, guestExpiryCopy, guestExpiryRelative }
}

const UserProfileCard = ({ userAuthContainer, onBack, onJoinEvent }) => {
  const { createdCopy } = buildAccountTimingCopy(userAuthContainer, { includeGuestExpiry: false })
  const userRoles = (userAuthContainer.roles?.() || []).filter(Boolean)
  const [verificationBusy, setVerificationBusy] = useState(false)
  const [verificationNotice, setVerificationNotice] = useState('')
  const [verificationError, setVerificationError] = useState(null)
  const profile = userAuthContainer.profile()
  const accountHandle = userAuthContainer.name?.() || ''
  const accountFullName = (userAuthContainer.fullName?.() || '').trim()
  const accountEmail = userAuthContainer.anearUser()?.email?.() || ''
  const profilePhone = String(profile.phone || '').trim()
  const homepageUrl = normalizeExternalUrl(profile.homepage)
  const profileBio = String(profile.bio || '').trim()
  const isEmailVerified = userAuthContainer.anearUser()?.emailVerified?.() ?? true

  const resendVerificationHandler = async () => {
    if (!accountEmail) return

    try {
      setVerificationBusy(true)
      setVerificationError(null)
      setVerificationNotice('')
      await anearApi.requestEmailVerification(accountEmail)
      setVerificationNotice(`Verification email sent to ${accountEmail}.`)
    } catch (error) {
      setVerificationError(error)
    } finally {
      setVerificationBusy(false)
    }
  }

  return (
    <>
      <Paper
        sx={{
          width: 'auto',
          display: 'block',
          m: '0 auto',
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.15, sm: 1.35 },
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.2)',
          boxShadow: '0 18px 40px rgba(2,6,23,0.45), inset 0 1px 0 rgba(255,255,255,0.03)',
          mt: 0.5,
          maxWidth: {
            xs: '100%',
            sm: '30rem',
            md: '34rem'
          }
        }}
      >
        {onBack && <PageBackRow onClick={onBack} sx={{ mb: 0.35 }} />}
        {!isEmailVerified && (
          <Alert
            severity="warning"
            sx={{
              mb: 1.35,
              mx: 'auto',
              maxWidth: '18rem',
              bgcolor: 'rgba(245, 158, 11, 0.09)',
              color: '#FDE68A',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: 999,
              px: 0.7,
              '& .MuiAlert-message': {
                width: '100%',
                minWidth: 0,
                py: 0,
              },
              '& .MuiAlert-icon': {
                color: '#FBBF24',
                fontSize: '1rem',
                py: 0.15,
                mr: 0.9
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.9
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>
                Email not Verified
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={resendVerificationHandler}
                disabled={verificationBusy || !accountEmail}
                sx={{
                  minWidth: 0,
                  flexShrink: 0,
                  px: 1.15,
                  py: 0.38,
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: '#23160A',
                  background: 'linear-gradient(180deg, #FFE48A 0%, #FCD34D 52%, #EAAE1F 100%)',
                  border: '1px solid rgba(255,241,186,0.55)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.42), 0 2px 8px rgba(234,179,8,0.2)',
                  borderRadius: 999,
                  '&:hover': {
                    background: 'linear-gradient(180deg, #FFEB9F 0%, #FBBF24 55%, #D99712 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 12px rgba(234,179,8,0.28)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(35,22,10,0.45)',
                    background: 'rgba(252,211,77,0.42)',
                    borderColor: 'rgba(255,241,186,0.28)',
                    boxShadow: 'none'
                  }
                }}
              >
                {verificationBusy ? 'Sending...' : 'Resend'}
              </Button>
            </Box>
          </Alert>
        )}
        {verificationNotice && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              bgcolor: 'rgba(16,185,129,0.08)',
              color: '#D1FAE5',
              border: '1px solid rgba(16,185,129,0.25)'
            }}
          >
            {verificationNotice}
          </Alert>
        )}
        {verificationError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: '#FECACA',
              border: '1px solid rgba(239, 68, 68, 0.28)'
            }}
          >
            {verificationError.message || 'Unable to send verification email. Please try again.'}
          </Alert>
        )}
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: '18rem',
                mb: 1,
                p: 1.1,
                borderRadius: 3,
                bgcolor: 'rgba(15,23,42,0.86)',
                border: '1px solid rgba(148,163,184,0.16)',
                boxShadow: '0 12px 28px rgba(2,6,23,0.28), inset 0 1px 0 rgba(255,255,255,0.04)',
                '& .MuiAvatar-root': {
                  height: { xs: '11.5rem', sm: '13rem' },
                  width: { xs: '11.5rem', sm: '13rem' },
                  mx: 'auto',
                  borderRadius: 2.25,
                  boxShadow: '0 10px 24px rgba(2,6,23,0.4)'
                }
              }}
            >
              <CurrentUserAvatar size={'hugeAvatar'} />
            </Box>
            {accountHandle && (
              <Typography
                sx={{
                  color: '#D7E3F8',
                  fontSize: { xs: '1.08rem', sm: '1.22rem' },
                  fontWeight: 650,
                  letterSpacing: 0.15,
                  lineHeight: 1.15,
                  mb: 0.52,
                  textShadow: '0 2px 8px rgba(2,6,23,0.7)'
                }}
              >
                {accountHandle}
              </Typography>
            )}
            <Typography
              sx={{
                mt: 0,
                mb: 1.45,
                fontWeight: 800,
                letterSpacing: 0.2,
                fontSize: { xs: '2rem', sm: '2.55rem' },
                lineHeight: 1.02,
                background: 'linear-gradient(180deg, #FFF3BF 0%, #E9C86F 38%, #D19A32 70%, #A76816 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                WebkitTextStroke: '0.45px rgba(255,248,220,0.72)',
                textShadow: '0 1px 0 rgba(255,250,235,0.28), 0 2px 6px rgba(2,6,23,0.28)'
              }}
              component="h2"
            >
              {accountFullName || accountHandle}
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: '20rem',
                mb: userRoles.length > 0 ? 1.5 : 0,
                px: { xs: 1.4, sm: 1.7 },
                py: 1.35,
                borderRadius: 2.5,
                textAlign: 'left',
                bgcolor: 'rgba(15,23,42,0.7)',
                border: '1px solid rgba(148,163,184,0.18)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)'
              }}
            >
              {profilePhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                  <PhoneOutlinedIcon sx={{ color: 'rgba(148,163,184,0.82)', fontSize: '1.45rem', flexShrink: 0 }} />
                  <Box
                    component="a"
                    href={`tel:${profilePhone}`}
                    sx={{
                      textDecoration: 'none',
                      wordBreak: 'break-word',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <Typography component="span" sx={{ color: 'rgba(191,219,254,0.72)', fontSize: { xs: '0.74rem', sm: '0.78rem' }, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Phone:
                    </Typography>
                    <Typography component="span" sx={{ ml: 0.8, color: '#E6EEF9', fontSize: { xs: '0.95rem', sm: '1.03rem' }, fontWeight: 560 }}>
                      {profilePhone}
                    </Typography>
                  </Box>
                </Box>
              )}
              {accountEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0, mt: profilePhone ? 0.7 : 0 }}>
                  <EmailOutlinedIcon sx={{ color: 'rgba(148,163,184,0.82)', fontSize: '1.45rem', flexShrink: 0 }} />
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    <Box component="span" sx={{ color: 'rgba(191,219,254,0.72)', fontSize: { xs: '0.74rem', sm: '0.78rem' }, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Email:
                    </Box>
                    <Box component="span" sx={{ ml: 0.8, color: '#E6EEF9', fontSize: { xs: '0.95rem', sm: '1.03rem' }, fontWeight: 560 }}>
                      {accountEmail}
                    </Box>
                  </Typography>
                </Box>
              )}
              {createdCopy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0, mt: accountEmail || profilePhone ? 0.7 : 0 }}>
                  <CalendarMonthOutlinedIcon sx={{ color: 'rgba(148,163,184,0.82)', fontSize: '1.45rem', flexShrink: 0 }} />
                  <Typography>
                    <Box component="span" sx={{ color: 'rgba(191,219,254,0.72)', fontSize: { xs: '0.74rem', sm: '0.78rem' }, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      Joined:
                    </Box>
                    <Box component="span" sx={{ ml: 0.8, color: '#E6EEF9', fontSize: { xs: '0.95rem', sm: '1.03rem' }, fontWeight: 560 }}>
                      {createdCopy.replace('Account created ', '')}
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          {userRoles.length > 0 && (
            <Box sx={{ mt: 0.25, mb: 1.5, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(230,233,239,0.72)', fontSize: '0.8rem', mb: 0.75 }}>
                Roles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                {userRoles.map((role) => (
                  <Box
                    key={role}
                    sx={{
                      px: 1.1,
                      py: 0.35,
                      borderRadius: 999,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      color: '#C4B5FD',
                      bgcolor: 'rgba(124,58,237,0.22)',
                      border: '1px solid rgba(167,139,250,0.35)',
                      textTransform: 'uppercase'
                    }}
                  >
                    {String(role)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {homepageUrl && (
            <Box
              sx={{
                mt: 0.45,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(15,23,42,0.55)',
                border: '1px solid rgba(148,163,184,0.14)'
              }}
            >
              <Typography sx={{ color: 'rgba(230,233,239,0.58)', fontSize: '0.72rem', mb: 0.25 }}>
                URL
              </Typography>
              <Box
                component="a"
                href={homepageUrl}
                target="_blank"
                rel="noreferrer"
                sx={{
                  color: 'rgba(147,197,253,0.9)',
                  fontSize: '0.84rem',
                  lineHeight: 1.55,
                  textDecoration: 'none',
                  wordBreak: 'break-all',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {homepageUrl}
              </Box>
            </Box>
          )}
          {profileBio && (
            <Box
              sx={{
                mt: 0.45,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(15,23,42,0.55)',
                border: '1px solid rgba(148,163,184,0.14)'
              }}
            >
              <Typography sx={{ color: 'rgba(230,233,239,0.54)', fontSize: '0.72rem', mb: 0.45 }}>
                Bio
              </Typography>
              <Typography sx={{ color: 'rgba(230,233,239,0.72)', fontSize: '0.84rem', lineHeight: 1.55 }} component="p">
                {profileBio}
              </Typography>
            </Box>
          )}
        </CardContent>
        <CardActions sx={{ p: 0, pt: 2.1, gap: 1.25, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box component={Link} sx={LinkStyling} to={ROUTES.UPDATE_PROFILE}>
            <Button
              size="small"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 800,
                lineHeight: 1.2,
                borderRadius: 999,
                px: 2.1,
                py: 0.7,
                color: '#0B1120',
                background: 'linear-gradient(135deg,#60A5FA 0%,#22D3EE 100%)',
                boxShadow: '0 0 18px rgba(56,189,248,0.38)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#3B82F6 0%,#06B6D4 100%)',
                  boxShadow: '0 0 24px rgba(56,189,248,0.52)'
                }
              }}
            >
              Edit Profile
            </Button>
          </Box>
          {onJoinEvent && (
            <Button
              size="medium"
              onClick={onJoinEvent}
              sx={{
                textTransform: 'none',
                fontSize: '1.02rem',
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#A5B4FC',
                px: 2.1,
                borderRadius: 999,
                border: '1px solid rgba(165,180,252,0.55)',
                '&:hover': {
                  borderColor: 'rgba(165,180,252,0.95)',
                  bgcolor: 'rgba(99,102,241,0.12)'
                }
              }}
            >
              Join Event
            </Button>
          )}
        </CardActions>
      </Paper>
    </>
  )
}

const GuestProfileCard = ({ userAuthContainer, onBack, onDeleteAccount }) => {
  const { createdRelative, guestExpiryRelative } = buildAccountTimingCopy(userAuthContainer, { includeGuestExpiry: true })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const openDeleteDialog = () => {
    setDeleteError(null)
    setDeleteDialogOpen(true)
  }

  const closeDeleteDialog = () => {
    if (deleteBusy) return
    setDeleteError(null)
    setDeleteDialogOpen(false)
  }

  const confirmDeleteAccount = async () => {
    if (!onDeleteAccount) return

    try {
      setDeleteBusy(true)
      setDeleteError(null)
      await onDeleteAccount()
    } catch (error) {
      setDeleteError(error)
      setDeleteBusy(false)
    }
  }

  return (
    <>
      <Paper
        sx={{
          width: 'auto',
          display: 'block',
          m: '0 auto',
          px: 3,
          pt: { xs: 1.35, sm: 1.6 },
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          mt: 0.25,
          position: 'relative',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        {onBack && <PageBackRow onClick={onBack} sx={{ mb: 0.5 }} />}
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              margin: '0px auto',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 1.2, sm: 1.1 },
              pt: { xs: 0.35, sm: 0.25 }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: { xs: '0 0 auto', sm: '0 0 42%' },
                width: { xs: '100%', sm: '42%' },
                '& .MuiAvatar-root': { height: 134, width: 134 }
              }}
            >
              <CurrentUserAvatar size={'hugeAvatar'} withName />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'stretch',
                textAlign: 'left',
                width: { xs: '100%', sm: '54%' },
                maxWidth: { xs: '100%', sm: '18rem' },
                mt: { xs: 0, sm: 2.2 },
                ml: { xs: 0, sm: 'auto' }
              }}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 1.1,
                  borderRadius: 2,
                  background:
                    'linear-gradient(90deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))',
                  border: '1px solid rgba(148,163,184,0.35)'
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr)',
                    columnGap: 1,
                    rowGap: 0.4,
                    alignItems: 'baseline'
                  }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(148,163,184,0.95)',
                      fontSize: '0.92rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.35
                    }}
                    variant="body2"
                  >
                    Created:
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(226,232,240,0.98)',
                      fontSize: '0.98rem',
                      fontWeight: 700,
                      lineHeight: 1.35,
                      overflowWrap: 'anywhere'
                    }}
                    variant="body2"
                  >
                    {createdRelative || '-'}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(148,163,184,0.95)',
                      fontSize: '0.92rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.35
                    }}
                    variant="body2"
                  >
                    Expires:
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(226,232,240,0.98)',
                      fontSize: '0.98rem',
                      fontWeight: 700,
                      lineHeight: 1.35,
                      overflowWrap: 'anywhere'
                    }}
                    variant="body2"
                  >
                    {guestExpiryRelative || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography
            sx={{ mt: 2.35, color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}
            gutterBottom
            variant="h5"
            component="h2"
          >
            Guest account
          </Typography>
          <Typography
            sx={{ color: 'rgba(230,233,239,0.8)', mt: 1 }}
            variant="body1"
          >
            You&apos;re currently using a temporary guest account. Guest accounts expire,
            so if you love this guest name or photo, register now to avoid losing them.
            When you register, you can keep this guest identity or switch to a new
            username and photo, like your real name and a selfie. You can also
            permanently delete this account and profile photo at any time.
          </Typography>
        </CardContent>
        <CardActions sx={{ p: 0, pt: 2.75, gap: 2, flexWrap: 'wrap' }}>
          <>
            <Box component={Link} sx={LinkStyling} to={ROUTES.SIGNUP}>
              <Button
                size="medium"
                sx={{
                  textTransform: 'none',
                  fontSize: '1.05rem',
                  fontWeight: 800,
                  lineHeight: 1.25,
                  color: '#0B1120',
                  px: 2.5,
                  borderRadius: 999,
                  background: 'linear-gradient(135deg,#22C55E 0%,#0EA5E9 100%)',
                  boxShadow: '0 0 22px rgba(56,189,248,0.55)',
                  '&:hover': {
                    background: 'linear-gradient(135deg,#16A34A 0%,#0284C7 100%)',
                    boxShadow: '0 0 28px rgba(56,189,248,0.75)'
                  }
                }}
              >
                Register this guest account
              </Button>
            </Box>
            <Button
              size="medium"
              onClick={openDeleteDialog}
              sx={{
                ml: 0.5,
                color: 'rgba(248,113,113,0.9)',
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.25,
                '&:hover': {
                  bgcolor: 'rgba(248,113,113,0.12)'
                }
              }}
            >
              Delete Account
            </Button>
          </>
        </CardActions>
      </Paper>
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete guest account?</DialogTitle>
        <DialogContent sx={{ pt: '0.5rem !important' }}>
          <Typography sx={{ color: 'text.secondary' }}>
            Are you sure you want to permanently delete this guest account? Your guest profile
            and location data will be scrubbed, and this guest identity cannot be recovered
            from this device once deleted.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError.message || 'Unable to delete guest account right now.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteAccount}
            disabled={deleteBusy}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {deleteBusy ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ProfileCard = ({ userAuthContainer, onBack, onDeleteAccount }) => {

  const profileCard = userAuthContainer => {
    if (userAuthContainer.isRegisteredAuthed()) {
      return <UserProfileCard userAuthContainer={userAuthContainer} onBack={onBack} />
    } else {
      return <GuestProfileCard userAuthContainer={userAuthContainer} onBack={onBack} onDeleteAccount={onDeleteAccount} />
    }
  }

  return userAuthContainer.isAuthed() && profileCard(userAuthContainer)
}

const ViewProfilePage = () => {
  const userAuthContainer = UserAuthContainer.useContainer()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleAuthTokenExpired } = useNavigation()

  const eventReturnPath = (() => {
    const path = location.state?.from?.pathname
    if (typeof path !== 'string') return null
    if (path.startsWith('/e/') || path.startsWith('/p/')) return path
    return null
  })()

  React.useEffect(() => {
    // Force reload user data to ensure we have the latest claimed_at
    // and other timestamps, especially for recycled guest accounts.
    userAuthContainer.loadUser(true).catch((err) => {
      if (!handleAuthTokenExpired(err, userAuthContainer)) {
        // Keep profile view resilient for non-auth refresh failures.
        // eslint-disable-next-line no-console
        console.error(err)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBack = () => {
    if (userAuthContainer.isRegisteredAuthed()) {
      if (userAuthContainer.isMerchant()) {
        navigate(ROUTES.MERCHANT_DASHBOARD)
      } else {
        navigate(ROUTES.APPS)
      }
    } else {
      navigate(-1)
    }
  }

  const handleJoinEvent = () => {
    if (!eventReturnPath) return
    navigate(eventReturnPath, {
      state: location.state?.from?.state,
      replace: true,
    })
  }

  const handleDeleteGuestAccount = async () => {
    const user = userAuthContainer.anearUser()
    if (!user?.id) throw new Error('Guest account not available')

    try {
      await anearApi.deleteAccount(user.id)
      userAuthContainer.clearUserAndToken()
      navigate(ROUTES.APPS, { replace: true })
    } catch (err) {
      if (!handleAuthTokenExpired(err, userAuthContainer)) {
        throw err
      }
    }
  }

  return (
    <Box sx={contentColumnPageShellSx}>
      {userAuthContainer.isRegisteredAuthed()
        ? <UserProfileCard userAuthContainer={userAuthContainer} onBack={handleBack} onJoinEvent={eventReturnPath ? handleJoinEvent : null} />
        : <ProfileCard userAuthContainer={userAuthContainer} onBack={handleBack} onDeleteAccount={handleDeleteGuestAccount} />}
    </Box>
  )
}

export default ViewProfilePage
